/// <reference types="vite/client" />
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key)
        } catch {
          return sessionStorage.getItem(key)
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value)
        } catch {
          sessionStorage.setItem(key, value)
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key)
        } catch {
          sessionStorage.removeItem(key)
        }
      }
    },
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: 'kova-auth',
  }
})

// Types
export interface Profile {
  id: string
  first_name: string
  last_name: string
  bio?: string
  phone?: string
  country_code?: string
  specialties?: string[]
  top_countries?: string[]
  profile_picture_url?: string
  agency_name?: string | null
  iata_number?: string | null
  iata_verified: boolean
  account_status: 'pending' | 'approved' | 'rejected'
  role?: string
  feature_flags?: Record<string, boolean>
  preferred_language?: 'en' | 'es'
  created_at: string
  updated_at: string
}

export interface SignupData {
  email: string
  password: string
  firstName: string
  lastName: string
  agency: string
  iata: string
}

// Cache for user names to avoid repeated database calls
const userNameCache: Record<string, string> = {}

/**
 * Fetch a user's full name by their ID
 * Returns the cached name if available, otherwise fetches from database
 */
export async function getUserNameById(userId: string): Promise<string | null> {
  if (!userId) return null

  // Check cache first
  if (userNameCache[userId]) {
    return userNameCache[userId]
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', userId)
      .single()

    if (error || !data) {
      return null
    }

    const fullName = `${data.first_name || ''} ${data.last_name || ''}`.trim()

    // Cache the result
    if (fullName) {
      userNameCache[userId] = fullName
    }

    return fullName || null
  } catch {
    return null
  }
}
